import { memo, useState, useEffect } from 'react';

import {
  Button,
  FormControl,
  TextField,
  IconButton,
  Box,
  Typography,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Enum_Transapi_Locale,
  Enum_Translation_Locale,
  useSendAdHocQuestionsMutation,
  useUpdatePatientVisitAddAdhocQuestionMutation,
} from '@cyren/common-lib';
import { size } from 'lodash';
import { TPatientVisit, TTransApi } from '../../../graphql-enhanced';
import { onError } from '../../../utils/apollo-utils';
import { toast } from 'react-toastify';
import { genMessageLongId } from '../../nav-utils';
import { SubmitButtonState } from './ModalSendCommunication';
import { useUserTranslateStrings } from '../../../report/use-user-translate-strings';
import { AdhocQuestionType } from '../../../patient/patient-types';
import { useStaticLocales } from '../../hooks/locales/use-static-locale';
import { useMutation } from '@tanstack/react-query';
import { useIsV2Path } from '../../../app/route-utils';
import { sendAdhocMessagesByVisitId } from '../../../v2/api/patient-adhoc';

function PanelAddAdHocQuestions(props: {
  onClose: () => void;
  visit: TPatientVisit;
  setSubmitButtonState: (newState: SubmitButtonState) => void;
}) {
  const { sltStr } = useStaticLocales();
  const { onClose, visit, setSubmitButtonState } = props;
  const isV2Path = useIsV2Path();

  const patientLocale: Enum_Transapi_Locale = (visit?.surveyState.reportState?.locale ||
    'english') as Enum_Transapi_Locale;

  const updateSubmitButtonState = () => {
    setSubmitButtonState({
      loading: addingAdhoc || sending || sendingV2,
      enabled: size(pendingAdHocQuestions) > 0,
      message: sltStr({ key: 'comm-btn-send' }),
      callback: handleSave,
    });
  };

  useEffect(() => {
    updateSubmitButtonState();
  }, []);

  const [newAdHocQuestion, setNewAdHocQuestion] = useState<string>('');
  const [pendingAdHocQuestions, setPendingAdHocQuestions] = useState<AdhocQuestionType[]>([]);

  const [translateUserStrings, { loading: translating }] = useUserTranslateStrings(
    (results: TTransApi[]) => {
      // add any newly received translated strings into the map, but keep the old ones as well
      const key = newAdHocQuestion;
      const result = results.find((t) => t.target === key);
      if (result) {
        setPendingAdHocQuestions([
          ...pendingAdHocQuestions,
          {
            type: 'text',
            questionText: key,
            translatedQuestionText: result.output,
            uniqueId: genMessageLongId(),
          },
        ]);
      }
      setNewAdHocQuestion('');
    },
    patientLocale,
  );

  const [addAdhocQuestion, { loading: addingAdhoc }] =
    useUpdatePatientVisitAddAdhocQuestionMutation({
      onError,
    });

  const [sendAdHocSMS, { loading: sending }] = useSendAdHocQuestionsMutation({
    onError,
  });

  const { mutateAsync: sendV2Adhoc, isPending: sendingV2 } = useMutation({
    mutationFn: ({
      visitId,
      recipientAddress,
      uniqueId,
      adHocQuestions,
    }: {
      visitId: number;
      recipientAddress: string;
      uniqueId: string;
      adHocQuestions: AdhocQuestionType[];
    }) =>
      sendAdhocMessagesByVisitId(visitId, {
        recipientAddress,
        uniqueId,
        adHocQuestions: adHocQuestions.map((question) => ({
          type: question.type,
          questionText: question.questionText,
          translatedQuestionText: question.translatedQuestionText,
          uniqueId: question.uniqueId,
        })),
        messageSummary: sltStr({ key: 'adhoc-message-questions' }).replace(
          '{{count}}',
          size(adHocQuestions).toString(),
        ),
        contentDetails: adHocQuestions.map((question) => question.uniqueId),
      }),
    onError: (error: unknown) => {
      const fallback = 'Failed to send ad-hoc questions.';
      const apiMessageRaw = (error as { response?: { data?: { message?: unknown } } })?.response
        ?.data?.message;
      let apiMessage = '';
      if (Array.isArray(apiMessageRaw)) {
        apiMessage = apiMessageRaw.join(', ');
      } else if (typeof apiMessageRaw === 'string') {
        apiMessage = apiMessageRaw;
      }
      const message = apiMessage || (error as { message?: string })?.message || fallback;
      toast.error(message);
    },
  });

  useEffect(() => {
    updateSubmitButtonState();
  }, [pendingAdHocQuestions, addingAdhoc, sending, sendingV2]);

  const handleSave = () => {
    const visitId = Number(visit.id);

    if (!visit.surveyState.reportState?.systemValueMap?.phone) {
      throw new Error(sltStr({ key: 'adhoc-error-no-phone' }));
    }

    if (!Number.isFinite(visitId) || visitId <= 0) {
      throw new Error('Invalid patient visit id');
    }

    const recipientAddress = visit.surveyState.reportState?.systemValueMap?.phone as string;
    const messageUniqueId = genMessageLongId();

    if (isV2Path) {
      sendV2Adhoc({
        visitId,
        recipientAddress,
        uniqueId: messageUniqueId,
        adHocQuestions: pendingAdHocQuestions,
      }).then(() => {
        toast.success(sltStr({ key: 'resource-sent' }));
        onClose();
      });
      return;
    }

    addAdhocQuestion({
      variables: {
        id: visit.id!,
        data: {
          adHocState: {
            adHocQuestions: pendingAdHocQuestions,
          },
        },
      },
    }).then(() => {
      sendAdHocSMS({
        variables: {
          id: visit?.id as string,
          data: {
            messageContent: JSON.stringify({
              messageSummary: sltStr({ key: 'adhoc-message-questions' }).replace(
                '{{count}}',
                size(pendingAdHocQuestions).toString(),
              ),
              contentDetails: pendingAdHocQuestions.map((i) => i.uniqueId),
            }),
            recipientAddress,
            uniqueID: messageUniqueId,
          },
        },
      }).then(() => {
        toast.success(sltStr({ key: 'resource-sent' }));
        onClose();
      });
    });
  };

  return (
    <Box sx={{ flexDirection: 'column', margin: 2 }}>
      <Typography>{sltStr({ key: 'adhoc-panel-instruction' })}</Typography>
      <FormControl fullWidth sx={{ marginTop: 3, marginBottom: 3 }}>
        <TextField
          value={newAdHocQuestion}
          label={sltStr({ key: 'adhoc-label-question' })}
          placeholder={sltStr({ key: 'adhoc-placeholder-question' })}
          fullWidth
          disabled={translating}
          autoComplete='off' // disable auto-complete to prevent accidental PHI/PII leakage
          onChange={(e) => {
            setNewAdHocQuestion(e.target.value);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Button
                  variant='contained'
                  onClick={() => {
                    if (translating) return;
                    // @ts-ignore
                    if (patientLocale === 'english') {
                      setPendingAdHocQuestions([
                        ...pendingAdHocQuestions,
                        {
                          type: 'text',
                          questionText: newAdHocQuestion,
                          uniqueId: genMessageLongId(),
                        },
                      ]);
                      setNewAdHocQuestion('');
                    } else {
                      translateUserStrings([newAdHocQuestion], [Enum_Translation_Locale.English]);
                    }
                  }}
                  color='primary'
                  disabled={!size(newAdHocQuestion)}
                >
                  {translating && <span className='loading loading-spinner'></span>}
                  {sltStr({ key: 'adhoc-btn-add' })}
                </Button>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>
      <Box>
        <Typography variant='caption' className='opacity-80'>
          {sltStr({ key: 'adhoc-panel-translation-note' })}
        </Typography>
      </Box>
      {size(pendingAdHocQuestions) > 0 && (
        <Box sx={{ marginTop: 2 }}>
          <hr style={{ marginBottom: '1rem' }} />
          {pendingAdHocQuestions.map((question) => {
            return (
              <Box
                key={question.uniqueId}
                sx={{ display: 'flex', flexDirection: 'row', width: '100%', alignItems: 'center' }}
              >
                <Typography>{question.questionText}</Typography>
                <IconButton
                  aria-label='close'
                  onClick={() => {
                    setPendingAdHocQuestions(
                      pendingAdHocQuestions.filter((i) => i.uniqueId !== question.uniqueId),
                    );
                  }}
                  sx={{
                    marginLeft: 'auto',
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default memo(PanelAddAdHocQuestions);
